﻿import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Pencil, Trash } from "lucide-react";
import { toast } from "react-toastify";

import CustomBadge from "@/components/desktop/badge/CustomBadge";
import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";
import BasicConfirm from "@/components/Confirm/BasicConfirm";

import useListController from "@/hooks/list/useListController";
import { useOATypeLookups, useOATypes, useDeleteOAType } from "@/hooks/maintainers/OA/OA-type/useOAType";

import { getApplicationOrderTypeVariant } from "@/constants/applicationOrderTypes";

import { base_filter, base_table } from "./oatype.constants";

import OATypeForm from "./Form/OATypeForm";

const OAType = () => {
	const navigate = useNavigate();
	const { estate_id, estate_parent_id } = useSelector((s) => s.auth.filter);

	const { data: lookups, isLoading: isLoadingLookups } = useOATypeLookups();

	const {
		isFilterOpen,
		filtersState,
		tableState,
		appliedFilterParams,
		reactTableSort,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		setFilterOptions,
	} = useListController({ baseFilter: base_filter, baseTable: base_table });

	const { data, isLoading } = useOATypes({
		estate_id,
		estate_parent_id,
		...appliedFilterParams,
		...tableState,
	});

	const { mutate: deleteOAType, isLoading: isLoadingDelete } = useDeleteOAType();

	useEffect(() => {
		if (lookups?.order_application_types) {
			setFilterOptions("type", lookups.order_application_types);
		}
	}, [lookups, setFilterOptions]);

	const [showDelete, setShowDelete] = useState(false);
	const [selected, setSelected] = useState(null);

	const handleDelete = () => {
		setShowDelete(false);

		deleteOAType(
			{
				selected,
				filters: {
					estate_id,
					estate_parent_id,
					...appliedFilterParams,
					...tableState,
				},
			},
			{
				onSuccess: () => {
					toast.success("Tipo de OA eliminado correctamente");
				},
				onError: () => {
					toast.error("Error al eliminar tipo de OA");
				},
			},
		);
	};

	const columns = useMemo(
		() => [
			{
				Header: "#",
				accessor: "",
				disableSortBy: true,
				width: "3%",
				Cell: ({ row }) => {
					return <span>{(tableState.page - 1) * tableState.per_page + row.index + 1}</span>;
				},
			},
			{
				Header: "Tipo de OA",
				accessor: "application_order_type",
				width: "10%",
				Cell: ({ value }) => {
					return (
						<CustomBadge variant={getApplicationOrderTypeVariant(value.value)} dot>
							{value.label}
						</CustomBadge>
					);
				},
			},
			{ Header: "Nombre", accessor: "name", width: "12%" },
			{ Header: "Descripción", accessor: "description", width: "15%" },
			{
				Header: "Campos",
				accessor: "estates_parents",
				width: "25%",
				Cell: ({ value }) => {
					return (
						<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
							{value.map((estate_parent) => {
								return <CustomBadge key={estate_parent.value}>{estate_parent.label}</CustomBadge>;
							})}
						</div>
					);
				},
			},
			{
				Header: "Bodegas",
				accessor: "estates",
				width: "25%",
				Cell: ({ value }) => {
					return (
						<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
							{value.map((estate) => {
								return <CustomBadge key={estate.value}>{estate.label}</CustomBadge>;
							})}
						</div>
					);
				},
			},
			{
				Header: "Acción",
				disableSortBy: true,
				accessor: "id",
				width: "10%",
				Cell: ({ value }) => {
					return (
						<CustomDropdown
							title="Acciones"
							items={[
								{
									label: "Editar",
									action: () => navigate("/configuracion/tipo_orden_aplicacion/editar/" + value),
									icon: Pencil,
								},
								{
									label: "Eliminar",
									action: () => {
										setSelected(value);
										setShowDelete(true);
									},
									icon: Trash,
									danger: true,
								},
							]}
							align="right"
						/>
					);
				},
			},
		],
		// eslint-disable-next-line
		[history],
	);

	return (
		<ListScaffold
			titleBreadcrumbs={[
				{ label: "Dashboard", onClick: () => navigate("/") },
				{ label: "Configuración", onClick: () => navigate("/configuracion") },
				{ label: "Tipo de OA", onClick: () => navigate("/configuracion/tipo_orden_aplicacion") },
			]}
			subtitle="Personaliza el nombre de cada tipo de OA y reutiliza su configuración"
			showCreate
			createLabel="Nuevo tipo de OA"
			onCreate={() => navigate("/configuracion/tipo_orden_aplicacion/crear")}
			actionButtons={[]}
			filterTitle="Filtrar tipos de OA"
			isFilterOpen={isFilterOpen}
			filters={filtersState}
			onFilterChange={handleFilterChange}
			onApplyFilters={handleApplyFilters}
			onClearFilters={handleClearAllFilters}
			onClearOneFilter={handleClearOneFilter}
			onOpenFilter={openFilter}
			onCloseFilter={closeFilter}
			isLoading={isLoading || isLoadingDelete || isLoadingLookups}
			data={data?.data || []}
			totalItems={data?.total || 0}
			totalPages={data?.last_page || 0}
			toItem={data?.to || 0}
			fromItem={data?.from || 0}
			pageIndex={tableState.page}
			columns={columns}
			onPageChange={handlePageChange}
			pageSize={tableState.per_page}
			onPageSizeChange={handlePageSizeChange}
			sortBy={reactTableSort}
			onSortByChange={handleSortByChange}
			pageSizeOptions={[20, 50, 100]}
			initialPageSize={tableState.per_page}
			initialSortBy={reactTableSort}
		>
			<OATypeForm
				filters={{
					estate_id,
					estate_parent_id,
					...appliedFilterParams,
					...tableState,
				}}
			/>

			<BasicConfirm
				showConfirm={showDelete}
				setShowConfirm={setShowDelete}
				handleAction={handleDelete}
				showValidation={true}
				textValidation={"eliminar tipo de OA"}
			/>
		</ListScaffold>
	);
};

export default OAType;
